// NoticeWritePage — /notice/new  and  /notice/:id/edit
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { useAuth } from "../../shared/stores/auth";
import { toast } from "../../shared/stores/toasts";

type Cat = { id: number; code: string; label: string; color: string; parent_id: number | null; min_rank: number };
type Attachment = { id: number; notice_id: number; filename: string; content_type: string | null; size: number; created_at: string };
type Notice = {
  id: number; category: string; title: string; body: string;
  is_pinned: boolean; author_id: number;
};

function fmtBytes(n: number) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// ── AI 내용 작성 모달 ────────────────────────────────────────────────────────────
function AiComposeModal({ title, onClose, onInsert }: {
  title: string;
  onClose: () => void;
  onInsert: (text: string, mode: "append" | "replace") => void;
}) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState("");
  const [model, setModel]   = useState("");

  const generate = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true); setResult("");
    try {
      const instruction =
        `당신은 사내 공지사항 작성 도우미입니다. 아래 요청을 바탕으로 공지 "본문"을 한국어로 작성하세요.\n` +
        `규칙:\n- 본문 텍스트만 출력하세요 (제목·"본문:" 같은 라벨·부연 설명·따옴표 금지).\n` +
        `- 적절한 단락과 줄바꿈으로 읽기 쉽게 구성하세요.\n- 정중하고 명확한 사내 공지 톤으로 작성하세요.\n\n` +
        `[공지 제목] ${title || "(미입력)"}\n[요청 내용] ${prompt.trim()}`;
      const r = await api.post<{ content: string; model_name: string }>("/ai/chat", {
        messages: [{ role: "user", content: instruction }],
      }, { timeout: 0 });
      setResult((r.data.content ?? "").trim());
      setModel(r.data.model_name ?? "");
    } catch (e: any) {
      toast.error("AI 생성 실패", e?.response?.data?.detail ?? e?.response?.data?.error?.message ?? "AI 모델 설정을 확인하세요");
    } finally { setBusy(false); }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 640, maxWidth: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "14px 18px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18 }}>✨</span>
          <h3 style={{ margin: 0, fontSize: 15, flex: 1 }}>AI로 내용 작성</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "var(--text-3)" }}>✕</button>
        </div>

        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14, overflowY: "auto" }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-3)", marginBottom: 6, display: "block" }}>
              어떤 공지를 작성할까요? <span style={{ fontWeight: 400 }}>핵심 내용·요점을 적어주세요</span>
            </label>
            <textarea
              className="t-input" autoFocus value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); generate(); } }}
              placeholder="예) 7월 15일 전사 워크샵 안내 — 장소 제주, 오전 9시 집합, 복장 자율, 참가 신청은 인사팀 메일로"
              rows={3}
              style={{ width: "100%", fontSize: 14, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", lineHeight: 1.7 }}
            />
            <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>Ctrl/⌘ + Enter 로 생성</div>
          </div>

          <button className="t-btn t-btn-primary" onClick={generate} disabled={busy || !prompt.trim()} style={{ alignSelf: "flex-start", padding: "0 20px", height: 38 }}>
            {busy ? "생성 중…" : (result ? "🔄 다시 생성" : "✨ 생성하기")}
          </button>

          {result && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-3)" }}>생성 결과 <span style={{ fontWeight: 400 }}>(수정 후 삽입할 수 있어요)</span></label>
                {model && <span style={{ fontSize: 10, color: "var(--text-3)", marginLeft: "auto" }}>{model}</span>}
              </div>
              <textarea
                className="t-input" value={result} onChange={e => setResult(e.target.value)}
                rows={10}
                style={{ width: "100%", fontSize: 14, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", lineHeight: 1.8 }}
              />
            </div>
          )}
        </div>

        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="t-btn" onClick={onClose}>닫기</button>
          <button className="t-btn" disabled={!result.trim()} onClick={() => onInsert(result.trim(), "append")}>본문에 추가</button>
          <button className="t-btn t-btn-primary" disabled={!result.trim()} onClick={() => onInsert(result.trim(), "replace")}>본문에 적용</button>
        </div>
      </div>
    </div>
  );
}

const WRITE_ROLES = new Set(["SUPER_ADMIN","HR_ADMIN","C_LEVEL","MANAGER","TEAM_LEAD","DIVISION_HEAD","CEO"]);
const ADMIN_ROLES = new Set(["SUPER_ADMIN","HR_ADMIN"]);

export default function NoticeWritePage() {
  const { id } = useParams<{ id: string }>();   // defined → edit mode
  const navigate  = useNavigate();
  const roles     = useAuth(s => s.manifest?.roles ?? s.user?.roles ?? []);
  const canWrite  = roles.some(r => WRITE_ROLES.has(r));
  const isAdmin   = roles.some(r => ADMIN_ROLES.has(r));
  const isEdit    = !!id;

  const [categories, setCategories] = useState<Cat[]>([]);
  const [title,   setTitle]   = useState("");
  const [body,    setBody]    = useState("");
  const [cat,     setCat]     = useState("GENERAL");
  const [pinned,  setPinned]  = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving,  setSaving]  = useState(false);
  const [files,    setFiles]    = useState<File[]>([]);          // 업로드 대기 파일
  const [existing, setExisting] = useState<Attachment[]>([]);    // (수정) 기존 첨부
  const [aiOpen,   setAiOpen]   = useState(false);               // ✨ AI 작성 모달

  const insertAi = (text: string, mode: "append" | "replace") => {
    setBody(prev => mode === "replace" ? text : (prev.trim() ? prev.trimEnd() + "\n\n" + text : text));
    setAiOpen(false);
    toast.success(mode === "replace" ? "본문에 적용했습니다" : "본문에 추가했습니다");
  };

  const tops = categories.filter(c => !c.parent_id);
  const childrenOf = (pid: number) => categories.filter(c => c.parent_id === pid);

  // Redirect if no write permission
  useEffect(() => {
    if (!canWrite) { navigate("/notice", { replace: true }); }
  }, [canWrite, navigate]);

  // Load categories — default to the first sub-tab (or first category) for new posts
  useEffect(() => {
    api.get<Cat[]>("/notice/categories")
      .then(r => {
        setCategories(r.data);
        if (!isEdit && r.data.length > 0) {
          const t0 = r.data.find(c => !c.parent_id);
          const kid = t0 ? r.data.find(c => c.parent_id === t0.id) : undefined;
          setCat((kid ?? t0)?.code ?? r.data[0].code);
        }
      })
      .catch(() => {});
  }, [isEdit]);

  // Load notice + existing attachments for edit
  useEffect(() => {
    if (!isEdit) return;
    api.get<Notice>(`/notice/${id}`)
      .then(r => {
        setTitle(r.data.title);
        setBody(r.data.body);
        setCat(r.data.category);
        setPinned(r.data.is_pinned);
      })
      .catch(() => navigate("/notice", { replace: true }))
      .finally(() => setLoading(false));
    api.get<Attachment[]>(`/notice/${id}/attachments`)
      .then(r => setExisting(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const uploadFiles = async (noticeId: number, list: File[]) => {
    for (const f of list) {
      const form = new FormData();
      form.append("file", f);
      try {
        await api.post(`/notice/${noticeId}/attachments`, form, { timeout: 0 });
      } catch (e: any) {
        toast.error(`첨부 실패: ${f.name}`, e?.response?.data?.error?.message ?? "");
      }
    }
  };

  const removeExisting = async (att: Attachment) => {
    if (!window.confirm(`"${att.filename}" 첨부를 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/notice/attachments/${att.id}`);
      setExisting(prev => prev.filter(a => a.id !== att.id));
    } catch { toast.error("첨부 삭제 실패"); }
  };

  const save = async () => {
    if (!title.trim()) { toast.error("제목을 입력하세요"); return; }
    if (!body.trim())  { toast.error("내용을 입력하세요"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/notice/${id}`, { title, body, category: cat, is_pinned: pinned });
        if (files.length) await uploadFiles(Number(id), files);
        toast.success("공지가 수정되었습니다");
        navigate(`/notice/${id}`);
      } else {
        const r = await api.post<Notice>("/notice", { title, body, category: cat, is_pinned: pinned });
        if (files.length) await uploadFiles(r.data.id, files);
        toast.success("공지가 등록되었습니다");
        navigate(`/notice/${r.data.id}`);
      }
    } catch (e: any) {
      toast.error(isEdit ? "수정 실패" : "등록 실패", e?.response?.data?.detail ?? "");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200, color:"var(--text-3)", fontSize:14 }}>
        불러오는 중…
      </div>
    );
  }

  return (
    <div style={{ maxWidth:860, margin:"0 auto", paddingBottom:48 }}>
      {/* ── Breadcrumb / Back ──────────────────────────────────────── */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20 }}>
        <button
          onClick={() => navigate(isEdit ? `/notice/${id}` : "/notice")}
          style={{
            display:"flex", alignItems:"center", gap:6,
            background:"none", border:"1px solid var(--border)",
            borderRadius:8, padding:"6px 14px", cursor:"pointer",
            fontSize:13, color:"var(--text-2)", transition:"background 0.12s",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = "var(--border)")}
          onMouseLeave={e => (e.currentTarget.style.background = "none")}
        >
          ← {isEdit ? "돌아가기" : "목록으로"}
        </button>
        <span style={{ fontSize:12, color:"var(--text-3)" }}>공지사항</span>
        <span style={{ fontSize:12, color:"var(--text-3)" }}>›</span>
        <span style={{ fontSize:12, color:"var(--text-3)" }}>{isEdit ? "공지 수정" : "공지 작성"}</span>
      </div>

      {/* ── Form ──────────────────────────────────────────────────── */}
      <div style={{
        background:"var(--surface)", border:"1px solid var(--border)",
        borderRadius:14, overflow:"hidden",
        boxShadow:"0 2px 8px rgba(0,0,0,0.06)",
      }}>
        {/* Form header */}
        <div style={{
          padding:"18px 28px 16px", borderBottom:"1px solid var(--border)",
          background:"rgba(0,0,0,0.015)",
        }}>
          <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:"var(--text)" }}>
            {isEdit ? "공지 수정" : "새 공지 작성"}
          </h2>
        </div>

        {/* Fields */}
        <div style={{ padding:"24px 28px", display:"flex", flexDirection:"column", gap:20 }}>
          {/* Category + pin */}
          <div style={{ display:"flex", gap:16, alignItems:"flex-end" }}>
            <div style={{ width:180 }}>
              <label style={{ fontSize:12, fontWeight:600, color:"var(--text-3)", marginBottom:6, display:"block", textTransform:"uppercase", letterSpacing:"0.05em" }}>
                카테고리
              </label>
              <select
                value={cat}
                onChange={e => setCat(e.target.value)}
                className="t-input"
                style={{ width:"100%", fontSize:13 }}
              >
                {tops.length === 0 && <option value="">(카테고리 없음)</option>}
                {tops.map(t => (
                  <optgroup key={t.id} label={t.label}>
                    <option value={t.code}>{t.label} 전체</option>
                    {childrenOf(t.id).map(s => <option key={s.id} value={s.code}>— {s.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            {isAdmin && (
              <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:14, paddingBottom:8 }}>
                <input
                  type="checkbox"
                  checked={pinned}
                  onChange={e => setPinned(e.target.checked)}
                  style={{ width:16, height:16, cursor:"pointer", accentColor:"var(--danger)" }}
                />
                <span style={{ color:"var(--text-2)" }}>📌 상단 고정</span>
              </label>
            )}
          </div>

          {/* Title */}
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:"var(--text-3)", marginBottom:6, display:"block", textTransform:"uppercase", letterSpacing:"0.05em" }}>
              제목
            </label>
            <input
              className="t-input"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="공지 제목을 입력하세요"
              style={{ width:"100%", fontSize:15, boxSizing:"border-box", fontWeight:500 }}
              onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }}
            />
          </div>

          {/* Body */}
          <div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
              <label style={{ fontSize:12, fontWeight:600, color:"var(--text-3)", textTransform:"uppercase", letterSpacing:"0.05em" }}>
                내용
              </label>
              <button type="button" className="t-btn" onClick={() => setAiOpen(true)}
                style={{ fontSize:12, padding:"4px 12px", display:"flex", alignItems:"center", gap:5 }}>
                ✨ AI로 내용 추가
              </button>
            </div>
            <textarea
              className="t-input"
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="공지 내용을 입력하세요"
              rows={14}
              style={{ width:"100%", fontSize:14, boxSizing:"border-box", resize:"vertical", fontFamily:"inherit", lineHeight:1.8, minHeight:200 }}
            />
          </div>

          {/* Attachments — 파일 업로드 */}
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:"var(--text-3)", marginBottom:6, display:"block", textTransform:"uppercase", letterSpacing:"0.05em" }}>
              첨부파일
            </label>
            {existing.map(a => (
              <div key={a.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", border:"1px solid var(--border)", borderRadius:8, marginBottom:6, fontSize:13 }}>
                <span>📎</span>
                <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.filename}</span>
                <span style={{ fontSize:11, color:"var(--text-3)" }}>{fmtBytes(a.size)}</span>
                <button type="button" onClick={() => removeExisting(a)} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--danger)", fontSize:12 }}>삭제</button>
              </div>
            ))}
            {files.map((f, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", border:"1px dashed var(--p-400,#818cf8)", borderRadius:8, marginBottom:6, fontSize:13, background:"rgba(99,102,241,0.05)" }}>
                <span>📎</span>
                <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
                <span style={{ fontSize:11, color:"var(--text-3)" }}>{fmtBytes(f.size)}</span>
                <button type="button" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--danger)", fontSize:12 }}>제거</button>
              </div>
            ))}
            <label style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"7px 14px", border:"1px solid var(--border)", borderRadius:8, cursor:"pointer", fontSize:13, color:"var(--text-2)", background:"var(--bg-0)" }}>
              ＋ 파일 추가
              <input type="file" multiple hidden onChange={e => { const fl = Array.from(e.target.files ?? []); setFiles(prev => [...prev, ...fl]); e.target.value = ""; }} />
            </label>
            <div style={{ fontSize:11, color:"var(--text-3)", marginTop:6 }}>파일당 최대 50MB · 등록 시 함께 업로드됩니다</div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding:"16px 28px", borderTop:"1px solid var(--border)",
          display:"flex", justifyContent:"flex-end", gap:8,
          background:"rgba(0,0,0,0.015)",
        }}>
          <button
            className="t-btn"
            onClick={() => navigate(isEdit ? `/notice/${id}` : "/notice")}
            disabled={saving}
          >
            취소
          </button>
          <button className="t-btn primary" onClick={save} disabled={saving} style={{ padding:"0 24px" }}>
            {saving ? "저장 중…" : (isEdit ? "수정 완료" : "등록하기")}
          </button>
        </div>
      </div>

      {aiOpen && (
        <AiComposeModal title={title} onClose={() => setAiOpen(false)} onInsert={insertAi} />
      )}
    </div>
  );
}
